import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  LayoutDashboard, Ticket, Package, BookOpen, MessageSquare, Users,
  LogOut, Menu, X, ShieldCheck, Laptop, BarChart3, Settings as SettingsIcon, Sun, Moon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

const roleBadges = {
  admin: { label: "Administrador", tone: "bg-blue-600 text-white" },
  tecnico: { label: "Técnico", tone: "bg-emerald-600 text-white" },
  cliente: { label: "Cliente", tone: "bg-zinc-700 text-white dark:bg-zinc-200 dark:text-zinc-900" },
};

const navFor = (role) => {
  const base = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/tickets", label: "Chamados", icon: Ticket, testid: "nav-tickets" },
  ];
  if (role !== "cliente") {
    base.push(
      { to: "/assets", label: "Ativos", icon: Laptop, testid: "nav-assets" },
      { to: "/inventory", label: "Estoque", icon: Package, testid: "nav-inventory" },
      { to: "/knowledge", label: "Base de Conhecimento", icon: BookOpen, testid: "nav-kb" },
      { to: "/chat", label: "Chat & WhatsApp", icon: MessageSquare, testid: "nav-chat" },
    );
  } else {
    base.push({ to: "/knowledge", label: "Base de Conhecimento", icon: BookOpen, testid: "nav-kb" });
  }
  if (role === "admin") {
    base.push(
      { to: "/reports", label: "Relatórios", icon: BarChart3, testid: "nav-reports" },
      { to: "/users", label: "Usuários", icon: Users, testid: "nav-users" },
      { to: "/settings", label: "Configurações", icon: SettingsIcon, testid: "nav-settings" },
    );
  } else if (role === "tecnico") {
    base.push({ to: "/reports", label: "Relatórios", icon: BarChart3, testid: "nav-reports" });
  }
  return base;
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!user) return null;
  const items = navFor(user.role);
  const badge = roleBadges[user.role] || roleBadges.cliente;
  const initials = (user.name || user.email || "?").slice(0, 2).toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const Sidebar = (
    <aside
      data-testid="sidebar"
      className="flex h-full w-full flex-col justify-between border-r border-border bg-card px-5 py-6"
    >
      <div>
        <Link to="/dashboard" className="flex items-center gap-2 px-1" data-testid="brand-link">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="leading-tight">
            <div className="font-heading text-lg font-semibold tracking-tight">HelpDesk</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">ITSM Suite</div>
          </div>
        </Link>

        <nav className="mt-10 flex flex-col gap-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "bg-accent text-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`
              }
            >
              <it.icon className="h-4 w-4" />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>

      <div className="mt-6 rounded-md border border-border p-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-primary text-primary-foreground text-xs">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium" data-testid="user-name">{user.name}</div>
            <div className="truncate text-xs text-muted-foreground" data-testid="user-email">{user.email}</div>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <Badge className={`${badge.tone} px-2 py-0.5 text-[10px] uppercase tracking-wider`} data-testid="user-role">
            {badge.label}
          </Badge>
          <Button size="sm" variant="ghost" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="flex min-h-screen">
      {/* Desktop sidebar */}
      <div className="hidden w-72 shrink-0 md:block">{Sidebar}</div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full w-72">{Sidebar}</div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-background/80 px-6 py-4 backdrop-blur-md md:px-10">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen((o) => !o)}
              data-testid="mobile-menu-btn"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Painel</div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleTheme}
              title={theme === "dark" ? "Mudar para Fundo Branco (Claro)" : "Mudar para Fundo Preto (Escuro)"}
              data-testid="theme-toggle-btn"
              className="gap-2 border-border bg-card hover:bg-accent"
            >
              {theme === "dark" ? (
                <>
                  <Sun className="h-4 w-4 text-amber-400" />
                  <span className="hidden sm:inline text-xs font-medium">Fundo Branco</span>
                </>
              ) : (
                <>
                  <Moon className="h-4 w-4 text-slate-700" />
                  <span className="hidden sm:inline text-xs font-medium">Fundo Preto</span>
                </>
              )}
            </Button>
            <div className="text-sm text-muted-foreground hidden md:block">
              Olá, <span className="font-medium text-foreground">{user.name.split(" ")[0]}</span>
            </div>
          </div>
        </header>

        <main className="flex-1 px-6 py-8 md:px-10 md:py-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
